import React, { useState } from "react";
import { toast } from "react-toastify";
import { SupportTicket, SupportTicketAPI } from "../../services/supportTicket.service";
import { AttachmentStager, StagedAttachment, uploadStaged } from "./AttachmentStager";

const MAX_ATTACHMENTS = 5;

const TYPE_LABELS: Record<string, string> = {
  voice_note: "Voice note",
  photo: "Photo",
  screen_recording: "Screen recording",
};

interface Props {
  ticket: SupportTicket;
  onClose: () => void;
  onSaved: (updated: SupportTicket) => void;
}

/** Edit the whole ticket: subject, description, and its original attachments. */
export const EditTicketModal: React.FC<Props> = ({ ticket, onClose, onSaved }) => {
  const [subject, setSubject] = useState(ticket.subject);
  const [description, setDescription] = useState(ticket.description);
  const [removed, setRemoved] = useState<Set<string>>(new Set());
  const [staged, setStaged] = useState<StagedAttachment[]>([]);
  const [saving, setSaving] = useState(false);

  const kept = ticket.attachments.filter(a => !removed.has(a.attachment_id));
  const slotsForNew = MAX_ATTACHMENTS - kept.length;

  const toggleRemoved = (id: string) =>
    setRemoved(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });

  const save = async () => {
    if (!subject.trim() || !description.trim()) {
      toast.error("Subject and description cannot be empty.");
      return;
    }
    setSaving(true);
    try {
      const added = await uploadStaged(staged, SupportTicketAPI.requestUploadUrls);
      const updated = await SupportTicketAPI.edit(ticket.ticket_id, subject.trim(), description.trim(), Array.from(removed), added);
      onSaved(updated);
      toast.success("Ticket updated.");
      onClose();
    } catch (err: any) {
      toast.error(err?.message || "Could not update the ticket.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4" onClick={onClose}>
      <div
        className="max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-2xl border-2 border-gray-300 bg-white p-5 shadow-2xl"
        onClick={e => e.stopPropagation()}
      >
        <div className="mb-4 flex items-center justify-between">
          <h3 className="text-base font-semibold text-gray-900">Edit ticket</h3>
          <button type="button" onClick={onClose} className="text-gray-400 hover:text-gray-700" aria-label="Close">✕</button>
        </div>

        <label className="mb-1 block text-xs font-medium text-gray-700">Subject</label>
        <input
          value={subject}
          onChange={e => setSubject(e.target.value)}
          maxLength={200}
          className="mb-3 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
        />

        <label className="mb-1 block text-xs font-medium text-gray-700">Description</label>
        <textarea
          value={description}
          onChange={e => setDescription(e.target.value)}
          maxLength={5000}
          rows={5}
          className="mb-3 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
        />

        <label className="mb-1 block text-xs font-medium text-gray-700">
          Attachments <span className="font-normal text-gray-400">({kept.length + staged.length}/{MAX_ATTACHMENTS})</span>
        </label>
        {ticket.attachments.length > 0 && (
          <ul className="mb-2 space-y-1.5">
            {ticket.attachments.map(a => {
              const gone = removed.has(a.attachment_id);
              return (
                <li key={a.attachment_id} className="flex items-center justify-between rounded-lg bg-gray-50 px-3 py-2 text-xs text-gray-700">
                  <span className={`truncate ${gone ? "text-gray-400 line-through" : ""}`}>
                    {TYPE_LABELS[a.attachment_type]} · {a.file_name}
                  </span>
                  <button
                    type="button"
                    onClick={() => toggleRemoved(a.attachment_id)}
                    className={`ml-2 flex-shrink-0 font-medium ${gone ? "text-blue-600 hover:text-blue-800" : "text-red-600 hover:text-red-800"}`}
                  >
                    {gone ? "Undo" : "Remove"}
                  </button>
                </li>
              );
            })}
          </ul>
        )}
        <AttachmentStager value={staged} onChange={setStaged} slots={slotsForNew} />

        <div className="mt-5 flex justify-end gap-2">
          <button type="button" onClick={onClose} className="rounded-lg border border-gray-300 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50">Cancel</button>
          <button
            type="button"
            onClick={save}
            disabled={saving}
            className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
          >
            {saving ? "Saving…" : "Save changes"}
          </button>
        </div>
      </div>
    </div>
  );
};
